import React from "react";
import { MRT_ColumnDef } from "material-react-table";
import { Box, Button, LinearProgress, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { EsgFileIcon, NewTabIcon } from "assets";
import { determineAlgorithmName, getDateFormatset, getDotColor, getInitials, getStatusLabel, isNullOrUndefined } from "utils";
import { COLORS, Avatar } from "components";
import { TABLE_HEADERS, EMPTY_CELL_VALUE } from "consts";
import { DocumentDTO } from "api";
import { Link } from "react-router-dom";
import { UTILITY_BILL_ALL_DATAENTRIES_VIEW } from "../document-summary-v2/utility-bill/utility-bill-allDataEntries-view/consts";

export const DOCUMENT_UPLOAD_PAGE = {
  ABSOLUTE_PATH: "/documents",
  PATH: "",
  TITLE: "Documents",
  ICON: <EsgFileIcon />,
  renderMenu: true,
};

export const ADD_DOCUMENT_BILL_PAGE = {
  TITLE: "Add document bill",
  ABSOLUTE_PATH: (documentId: string) =>
    `${DOCUMENT_UPLOAD_PAGE.ABSOLUTE_PATH}/${documentId}/add`,
  PATH: ":documentId/add",
  KEY: "documentId",
} as const;

export const BASE_DATA_ENTRIES = [
  {
    "label": "Warning (Conversion Error)",
    "name": "Warning (Conversion Error)",
    "value": 1
  },
  {
    "label": "Warning (Missing Fields)",
    "name": "Warning (Missing Fields)",
    "value": 2
  },
  {
    "label": "Warning (Potential Error Identified)",
    "name": "Warning (Potential Error Identified)",
    "value": 3
  },
  {
    "label": "Emissions Included (AI Generated)",
    "name": "Emissions Included (AI Generated)",
    "value": 4
  },
  {
    "label": "Emissions Included (User Confirmed)",
    "name": "Emissions Included (User Confirmed)",
    "value": 5
  },
  {
    "label": "Emissions Included (Excel Upload)",
    "name": "Emissions Included (Excel Upload)",
    "value": 7
  },
  {
    "label": "Approved (Signed Off)",
    "name": "Approved (Signed Off)",
    "value": 8
  },
  {
    "label": "Approved (Locked)",
    "name": "Approved (Locked)",
    "value": 9
  },
];

export const ACTIVE_FLOW_DATA_ENTRIES = [
  {
    "label": "Reviewed",
    "name": "Reviewed",
    "value": 6
  },
];

export const ACTIVE_ADDITIONAL_STATUS_DATA_ENTRIES = [
  {
    "label": "Not Applicable (Invalid Data)",
    "name": "Not Applicable (Invalid Data)",
    "value": 10
  },
  {
    "label": "Not Applicable (Cost Only)",
    "name": "Not Applicable (Cost Only)",
    "value": 11
  },
];

export const DATA_ENTRIES_STATUS_PRIORITY_MAP: Record<string, number> = {
  "Warning (Conversion Error)": 0,
  "Warning (Missing Fields)": 1,
  "Warning (Potential Error Identified)": 2,
  "Emissions Included (AI Generated)": 3,
  "Emissions Included (User Confirmed)": 4,
  "Approved (Signed Off)": 5,
  "Approved (Locked)": 6,
  "Reviewed": 7,
  "Emissions Included (Excel Upload)": 8,
  "Not Applicable (Invalid Data)": 9,
  "Not Applicable (Cost Only)": 10,
};

export const basicColorStatuses = [
  { label: "Warning", color: "#DD1515" },
  { label: "Emissions Included", color: "#9EE766" },
  { label: "Approved (Signed Off)", color: "#0E25D5" },
  { label: "Approved (Locked)", color: "#1D1D1D" },
];

export const approvalFlowColorStatuses = [
  { label: "Reviewed", color: "#00A1FF" },
];

export const additionalColorStatuses = [
  { label: "Not Applicable", color: "#7F7F7F" },
];

export const getDataUploadColumns = ({
  handleRowClick,
}: {
  handleRowClick: (row: any) => void,
}): MRT_ColumnDef<any>[] => [
    {
      id: "newTab",
      header: "",
      size: 20,
      accessorKey: "newTab",
      enableColumnActions: false,
      enableColumnFilter: false,
      Cell: ({ row }: any) => {
        const link = row?.original?.bills !== null && row?.original?.bills?.length > 0
          ? UTILITY_BILL_ALL_DATAENTRIES_VIEW.ABSOLUTE_PATH(row?.original?.bills[0]?.id)
          : ADD_DOCUMENT_BILL_PAGE.ABSOLUTE_PATH(row?.original?.id);

        return (
          <Tooltip title="Open bill in a new tab" placement="top">
            <Link
              to={`${link}`}
              target="_blank"
              style={{
                maxWidth: "20px",
                padding: "0px",
              }}
            >
              <Button
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: "20px",
                  maxWidth: "20px",
                  padding: "0px",
                  "& span": {
                    width: "0px",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(row);
                }}
              >
                <NewTabIcon />
              </Button>
            </Link>
          </Tooltip>
        );
      },
    },
    {
      header: "File name",
      Header: "File name",
      accessorKey: "fileName",
      Cell: ({ cell }: any) => {
        const value = cell?.getValue() as string;
        return (
          <Tooltip title={value}>
            <Box
              sx={{
                display: "block", // Ensure it's a block-level element
                width: "200px", // Set a fixed width
                overflow: "hidden",
                textOverflow: "ellipsis", // Show ellipsis if the text overflows
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
      size: 140,
    },
    {
      header: "Site",
      Header: <Box sx={{ paddingLeft: "12px" }}> Site</Box>,
      accessorKey: "site",
      Cell: ({ cell }: any) => {
        const value = cell?.getValue() as string;
        return (
          <Tooltip title={value}>
            <Box
              sx={{
                display: "block", // Ensure it's a block-level element
                width: "180px", // Set a fixed width
                overflow: "hidden",
                textOverflow: "ellipsis", // Show ellipsis if the text overflows
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
      size: 120,
    },
    {
      header: "Utility Type",
      Header: <Box sx={{ paddingLeft: "10px" }}>Utility Type</Box>,
      accessorKey: "utilityType",
      Cell: ({ cell }: any) => {
        const value = cell?.getValue() as string;

        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }

        return (
          <Tooltip title={value}>
            <Box
              sx={{
                paddingLeft: "12px",
                alignItems: "center",
                Width: "30px",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
      size: 140,
    },
    {
      accessorFn: (row: any) =>
        `${row?.uploader?.firstName} ${row?.uploader?.lastName}`,
      header: TABLE_HEADERS.uploader,
      Header: <Box sx={{ paddingLeft: "14px" }}>Uploader</Box>,
      id: "uploader",
      filterFn: (row: any, columnId: any, filterValue: any) => {
        const uploader = row.original?.uploader;
        const fullName = `${uploader?.firstName || ""} ${uploader?.lastName || ""}`.toLowerCase();
        const email = uploader?.email?.toLowerCase() || "";
        const search = filterValue.toLowerCase();

        return fullName.includes(search) || email.includes(search);
      },
      Cell: ({ cell, row }: any) => {
        const value = cell?.getValue();
        if (!value) {
          return EMPTY_CELL_VALUE;
        }
        return (
          <Tooltip title={row?.original?.uploader?.email}>
            <Box sx={{ paddingLeft: "14px" }}>
              <Avatar sx={{ width: "34px", height: "34px" }}>
                {getInitials(value)}
              </Avatar>
            </Box>
          </Tooltip>
        );
      },
      size: 150,
    },
    {
      header: "Uploaded at",
      Header: <Box sx={{ paddingLeft: "0px" }}>Uploaded at</Box>,
      accessorKey: "uploadDate",
      Cell: ({ cell }: any) => {
        const date: string = cell?.getValue() as string;
        const time = dayjs(date)?.utc()?.local()?.format("HH:mm");
        const value = dayjs(date)?.utc()?.local()?.format(getDateFormatset());
        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }

        return <Box>{`${value}, ${time}`}</Box>;
      },
      size: 160,
    },
    {
      header: "Pages",
      Header: <Box>Pages</Box>,
      accessorKey: "pages",
      Cell: ({ cell }: any) => {
        const value = cell?.getValue() as DocumentDTO["pages"];

        return (
          <Box sx={{ textAlign: "center", paddingLeft: "5px" }}>
            {value || EMPTY_CELL_VALUE}
          </Box>
        );
      },
      size: 100,
    },
    {
      id: "dataEntries",
      header: "Data entries",
      Header: <Box>Data Entries</Box>,
      Cell: ({ cell }: any) => {
        const value = cell?.getValue() as DocumentDTO["pages"];

        return (
          <Box sx={{ textAlign: "center", paddingLeft: "5px" }}>
            {value || EMPTY_CELL_VALUE}
          </Box>
        );
      },
      accessorFn: (originalRow: any) =>
        originalRow?.bills?.length === null ||
          originalRow?.bills?.length === undefined
          ? "-"
          : originalRow?.bills?.length,
      size: 140,
    },
    {
      header: "AI Extraction",
      Header: <Box>AI Extraction</Box>,
      accessorKey: "aiExtraction",
      filterFn: (row: any, columnId: any, filterValue: any) => {
        const value = row?.original?.aiExtraction;

        const normalized = String(filterValue).trim().toLowerCase().replace('%', '');

        if (normalized === "na") {
          return value === null || value === undefined;
        }

        return String(value) === String(normalized).trim();
      },
      Cell: ({ cell }: any) => {
        const value = cell?.getValue() as number;
        const isExcel = cell?.row?.original?.excel;

        return (
          <Box
            sx={{
              width: "100px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {(isNullOrUndefined(value) || isExcel) ? (
              <>
                <LinearProgress
                  sx={{
                    minWidth: "90px",
                    height: "8px",
                    borderRadius: "5px",
                    border: "2px solid #DCDCDC",
                    backgroundColor: "#f3f3f3",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#DCDCDC",
                    },
                  }}
                  variant="determinate"
                  value={100}
                />
                <p style={{ paddingLeft: "4px", fontSize: "12px" }}>N/A</p>
              </>
            ) : (
              <>
                <LinearProgress
                  sx={{
                    minWidth: "90px",
                    height: "8px",
                    borderRadius: "5px",
                    border: `1px solid ${COLORS.Mainblue}`,
                    backgroundColor: "#f3f3f3",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: COLORS.Mainblue,
                    },
                  }}
                  variant="determinate"
                  value={value}
                />
                <p
                  style={{ paddingLeft: "4px", fontSize: "12px" }}
                >{`${value}%`}</p>
              </>
            )}
          </Box>
        );
      },
      size: 150,
    },
    {
      header: "AI Algorithm",
      Header: <Box sx={{ paddingLeft: "12px" }}> AI Algorithm</Box>,
      accessorKey: "aiAlgorithm",
      Cell: ({ cell }: any) => {
        const row = cell.row.original;
        const value = determineAlgorithmName(row);

        return (
          <Tooltip title={value}>
            <Box
              sx={{
                paddingLeft: "12px",
                alignItems: "center",
                Width: "30px",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {value}
            </Box>
          </Tooltip>
        );
      },
      size: 200,
      filterFn: (row: any, id: any, filterValue: any) => {
        const algorithmName = determineAlgorithmName(row.original);
        return algorithmName.toLowerCase().includes(filterValue.toLowerCase());
      },
    },
    {
      header: "Data entries status",
      Header: <Box>Data Entries Status</Box>,
      accessorKey: "bills",
      filterFn: (row: any, id: any, filterValue: any) => {
        const status = row?.original?.dateEntryStatus;
        return status.toLowerCase().includes(filterValue.toLowerCase());
      },
      Cell: ({ row }: any) => {
        const status = row?.original?.dateEntryStatus;
        const missingFields = row?.original?.missingFields || [];
        const errorMessage = row?.original?.errorMessage;
        const priority = DATA_ENTRIES_STATUS_PRIORITY_MAP[status] ?? 9;

        const getTooltipMessage = () => {
          switch (priority) {
            case 0:
              return "There was an error converting the bill information to emissions data";
            case 1:
              return missingFields.length > 0
                ? (
                  <div style={{ textAlign: "left" }}>
                    {missingFields.map((t: string) => (
                      <div key={`key-${t}`}>• {t}</div>
                    ))}
                  </div>
                )
                : "";
            case 2:
              return errorMessage ? <span style={{ whiteSpace: "pre-line" }}>{errorMessage}</span> : "";
            case 3:
              return "Associated emissions from 100% AI extraction have been included";
            case 4:
              return "Associated emissions have been included following user confirmation of a warning message";
            case 5:
            case 6:
              return "This bill is for a site and time period that has been signed off or locked";
            default:
              return "";
          }
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: "4px",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Tooltip placement="top" title={getTooltipMessage()}>
              <span style={{ position: "relative" }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="12"
                  viewBox="0 0 5 5"
                  fill={getDotColor(priority)}
                  style={{ marginRight: "9px" }}
                >
                  <circle cx="2.5" cy="2.5" r="2.5" />
                </svg>
                {getStatusLabel(status, missingFields)}
              </span>
            </Tooltip>
          </Box>
        );
      },
      size: 220,
    }
  ];
